import { decodeHtml } from '~/js/utilities';
import { getProductIntegerId } from '~/js/utilities/product';
import {
  fetchProducts,
  formatPrice,
} from '~/js/utilities/products-and-collections';
import { getRandomKey, singularize } from '~/js/utilities/str';

const bundleBuilder = (
  bundleId,
  discount = 0,
  ids = [],
  groups = {},
  stepTitles = [],
  supportExtraItems = false,
  extraItemsLimit = 1,
  discountLabel = 'bundle[discount]'
) => ({
  drawerSide: 'bottom',
  bundleId: bundleId,
  discount: discount,
  groups: groups,
  isAddingToCart: false,
  productIds: ids,
  products: [],
  bundle: [],
  swatches: {},
  active_swatches: {},
  stepTitles: stepTitles,
  supportExtraItems: supportExtraItems,
  extraItemsLimit: extraItemsLimit,
  discountLabel: discountLabel,

  init() {
    try {
      const ids = this.productIds
        .filter(Boolean)
        .map((id) => getProductIntegerId(id));

      this.getProducts(ids);

      const storedBundleData = window.localStorage.getItem(
        `bundle-data-${this.bundleId}`
      );

      if (storedBundleData) {
        try {
          const data = JSON.parse(storedBundleData || '[]');
          if (!Array.isArray(data)) return;
          if (
            data?.every(
              (item) =>
                typeof item === 'string' &&
                item.match(/^(\d+)\:(\d+):(\d+):(step-\d+)$/)
            )
          ) {
            this.bundle = data;
          }
        } catch (error) {
          console.error(`Error processing the stored cart.`);
          console.error(error);
        } finally {
          if (!Array.isArray(this.bundle)) {
            this.bundle = [];
          }
        }
      }
    } catch (error) {
      console.error('Error parsing bundle data from local storage:', error);
    }

    this.$watch('isReadyToCart', (newValue) => {
      if (newValue) {
        this.openMobileSummaryDrawer();
      }
    });
  },

  get currentStep() {
    const limit = this.realNeededProductsIds?.length || 0;
    const currentState = this.bundle?.length;

    if (currentState <= 1) return 0;
    if (currentState >= limit) return 100;
    return Math.round(((currentState - 1) / (limit - 1)) * 100);
  },

  get productsInBundleSummary() {
    let cart = [];

    this.bundle.map((line, index) => {
      const [productIdInt, variantIdInt, _, step] = line.split(':');
      const itemStep = parseInt(step.replace('step-', ''));
      const product = this.products.find(
        (p) => getProductIntegerId(p.id).toString() === productIdInt
      );

      if (product) {
        const variant = product.variants.find(
          (v) =>
            getProductIntegerId(v.id, 'ProductVariant').toString() ===
            variantIdInt
        );

        if (variant) {
          const cartItem = {
            handle: `#product-${itemStep}`,
            step: itemStep,
            key: `cart-${index}-${product.id}`,
            id: product.id,
            image: product.media[0]?.url,
            title: product.title,
            variantId: variant.id,
            options: variant.selectedOptions || [],
            price: parseFloat(variant.price.amount),
          };

          cart.push(cartItem);
        }
      }
    });

    if (cart.length < this.realNeededProductsIds.length) {
      // Add empty items to the cart to complete the required length (this.productIds.length)
      const emptyItems = this.realNeededProductsIds
        .slice(cart.length)
        .map((id, index) => ({
          handle: `#product-${index + 1}`,
          step: index + 1,
          key: `cart-${index}-${id}`,
          image: '',
          title: '',
          variantId: null,
          options: [],
          price: 0,
        }));

      cart.push(...emptyItems);
    }

    return cart;
  },

  get visibleProducts() {
    const visibleProducts = (this.realNeededProductsIds || [])
      .map((id, idx) => {
        const step = idx + 1;
        let productId = getProductIntegerId(id).toString();
        if (this.active_swatches[step]) {
          const activeSwatch = this.active_swatches[step];
          productId = activeSwatch;
        }

        return this.products.find(
          (p) => getProductIntegerId(p.id).toString() === productId.toString()
        );
      })
      .filter(Boolean)
      .filter((product) => !!product.id)
      .map((product, idx) => ({ ...product, step: idx + 1 }));

    return visibleProducts;
  },

  get isReadyToCart() {
    const neededIdsCount = this.realNeededProductsIds.length;
    return this.bundle?.length >= neededIdsCount;
  },

  get summaryButtonLabel() {
    if (this.isAddingToCart) {
      return 'Adding...';
    }

    if (!this.isReadyToCart) {
      return 'Add to Bundle';
    }

    return 'Add to Cart';
  },

  get summaryTotal() {
    let total = 0;

    this.productsInBundleSummary.forEach((product) => {
      total += parseFloat(product.price);
    });

    return total;
  },

  get discountedTotal() {
    if (!this.isBundleFull) return 0;
    return this.summaryTotal - this.summaryTotal * (this.discount / 100);
  },

  get formattedDiscount() {
    return formatPrice({
      amount: this.discountedTotal,
      currencyCode: this.currencyCode,
    });
  },

  get currencyCode() {
    if (!this.products || this.products.length === 0) return 'USD';
    return this.products[0].priceRange.minVariantPrice.currencyCode;
  },

  get formattedTotal() {
    return formatPrice(
      { amount: this.summaryTotal, currencyCode: this.currencyCode },
      false
    );
  },

  get bundleDiscount() {
    if (!this.products || this.products.length === 0) return '';
    const saveTotal = this.summaryTotal - this.discountedTotal;

    return `Save ${formatPrice({
      amount: saveTotal,
      currencyCode: this.currencyCode,
    })} with your bundle`;
  },

  get isEmptyBundle() {
    return this.bundle.length === 0;
  },

  get realNeededProductsIds() {
    if (this.extraItemsLimit > 1 && this.supportExtraItems) {
      return this.productIds.reduce((ids, id) => {
        const size = this.extraItemsLimit;
        const repeated = Array.from({ length: size }).map((_) => id);
        return ids.concat(repeated);
      }, []);
    }

    return this.productIds;
  },

  get isBundleFull() {
    return this.bundle.length >= this.productIds.length;
  },

  get showBundleKey() {
    const params = new URLSearchParams(window.location.search);
    return params.has('show_code') && params.get('show_code') === 'true';
  },

  get bundleKey() {
    const keys = [
      this.bundleId,
      this.discount,
      this.realNeededProductsIds.join(','),
      this.products
        .map((product) => getProductIntegerId(product.id))
        .filter((pid) => !this.productIds.includes(pid))
        .join(','),
    ];

    return keys.filter(Boolean).join('||');
  },

  removeProductFromBundle(productId, variantId, step) {
    try {
      const productIdInt = getProductIntegerId(productId);
      const variantIdInt = getProductIntegerId(variantId, 'ProductVariant');
      const parentId = this.products.find(
        (p) => getProductIntegerId(p.id) === getProductIntegerId(productId)
      )?.parentProductId;
      const parentIdInt = getProductIntegerId(parentId);
      const index = this.bundle.findIndex(
        (item) =>
          item === `${productIdInt}:${variantIdInt}:${parentIdInt}:step-${step}`
      );

      if (index !== -1) {
        const bundle = [...this.bundle];
        bundle.splice(index, 1);
        this.bundle = [...bundle];
      }

      window.localStorage.setItem(
        `bundle-data-${this.bundleId}`,
        JSON.stringify(this.bundle)
      );

      const deletedStep = document.getElementById(`product-${step}`);

      if (deletedStep) {
        deletedStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.log('Error updating bundle');
    }
  },

  async addProductToBundle(productId, variantId, step) {
    this.isWorking = true;
    const bundle = this.bundle;
    const productIdInt = getProductIntegerId(productId);
    const variantIdInt = getProductIntegerId(variantId, 'ProductVariant');
    const parentId = this.products.find(
      (p) => getProductIntegerId(p.id) === getProductIntegerId(productId)
    )?.parentProductId;
    const parentIdInt = getProductIntegerId(parentId);

    bundle.push(`${productIdInt}:${variantIdInt}:${parentIdInt}:step-${step}`);
    this.bundle = [...bundle];

    window.localStorage.setItem(
      `bundle-data-${this.bundleId}`,
      JSON.stringify(this.bundle)
    );

    this.isWorking = false;
  },

  async addBundleToCart() {
    try {
      this.isAddingToCart = true;
      const bundleKey = getRandomKey(8);
      const items = this.bundle.map((item) => {
        const [_, variantId] = item.split(':');
        const itemKey = getRandomKey(8);

        return {
          id: getProductIntegerId(variantId, 'ProductVariant').toString(),
          quantity: 1,
          properties: {
            __bundle_unique_key: itemKey,
            __bundle_product: this.bundleId,
            __bundle_key: bundleKey,
            __bundle_discount_label: this.discountLabel,
          },
        };
      });

      if (items.length === this.bundle.length) {
        await this.$store.cart.addToCart({
          items: items,
        });

        this.bundle = [];
        window.localStorage.removeItem(`bundle-data-${this.bundleId}`);
        this.$dispatch('open-cart');
        this.$dispatch('clear-product-form');
      }
    } catch (e) {
      console.log(e);
      alert('Sorry, there was an error updating your cart.');
    } finally {
      this.isAddingToCart = false;
    }
  },

  setActiveSwatch(step, swatchId) {
    console.log({ step, swatchId });
    this.active_swatches[step] = swatchId;
  },

  async getProducts(ids, parentProductId) {
    try {
      // Filter out products that are already in the products array
      const idsToFetch = ids.filter(
        (id) => !this.products.some((p) => p.id === id)
      );
      const products = await fetchProducts(idsToFetch);
      const data = ids
        .map((id, idx) => {
          const product = products[id];
          if (!product) {
            return null;
          }
          let step = idx + 1;

          if (parentProductId) {
            // Search the step of the parent ID
            const parentProduct = this.products.find(
              (p) => p.id === parentProductId
            );
            if (parentProduct) {
              step = parentProduct.step;
            }
          }

          let stepsTitle =
            this.stepTitles?.length > 0 ? this.stepTitles[idx] : null;

          if (this.stepTitles?.length === 1) {
            stepsTitle = this.stepTitles[0];
          }

          return {
            ...product,
            step: step,
            pickTitle:
              stepsTitle ||
              `Pick your ${
                product.type?.value
                  ? singularize(product.type?.value)
                  : 'product'
              }`,
            isVisible: parentProductId ? false : true,
            parentProductId: parentProductId || product.id,
            media: product.images?.nodes || [],
          };
        })
        .filter(Boolean);

      data.forEach((product) => {
        const exists = this.products.some((p) => product.id === p.id);
        if (!exists) {
          this.products.push(product);
        }
      });

      if (!parentProductId) {
        this.products.forEach((product, idx) => {
          const id = getProductIntegerId(product.id);
          if (this.groups[id]) {
            this.queryGroupedProducts(product, idx);
          }
        });
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  },

  async queryGroupedProducts(mainProduct, index) {
    const currentProductId = getProductIntegerId(mainProduct.id);
    const groupedIds = this.groups[currentProductId];

    if (!groupedIds) {
      return;
    }

    try {
      await this.getProducts(groupedIds, mainProduct.id);

      const swatch_objects = groupedIds.map((id, index) => {
        const swatchProduct = this.products.find(
          (p) => getProductIntegerId(p.id) === getProductIntegerId(id)
        );

        return {
          index: index,
          name: decodeHtml(swatchProduct.title || ''),
          swatch_image: swatchProduct.images.nodes.at(-1).url,
          main_product_id: getProductIntegerId(mainProduct.id),
          item_id: getProductIntegerId(swatchProduct.id),
          product_url: `${window.Shopify.routes.root}products/${swatchProduct.handle}`,
          main_image: swatchProduct.images.nodes.at(0).url,
          hover_image: swatchProduct.images.nodes.at(1).url,
          title: swatchProduct.description?.value || swatchProduct.title,
          tags: swatchProduct.tags,
          brand: swatchProduct.brand?.value || '',
          print: swatchProduct.print?.value || '',
        };
      });

      this.swatches[mainProduct.id.toString()] = swatch_objects;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
    }
  },

  openMobileSummaryDrawer() {
    if (window.innerWidth > 1023) return false; // do not change anything if not mobile

    this.$refs.drawer.querySelector('dialog').showModal();
  },

  closeSummaryDrawer() {
    if (window.innerWidth > 1023) return false; // do not change anything if not mobile

    this.$refs.drawer.querySelector('dialog').close();
  },
});

export default bundleBuilder;
